import React, { useState, useEffect } from 'react';
import { Tasker } from 'common/src/utils/types';
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Button,
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from '@nextui-org/react';
import ModalViewTasker from '../../components/specifics/taskers/ModalViewTasker';
import { toast } from 'react-toastify';
import { fetchTaskerCategories } from 'common/src/api/taskers_categories';
import { fetchCategories } from 'common/src/api/categories';
import { fetchPendingTaskersPaginated, approveTasker, rejectTasker } from 'common/src/api/taskers';
import { SkeletonLoader } from '../../components/skeletonLoader/SkeletonLoader';
// import { sendTaskerStatusEmail } from '../../utils/taskerStatus';
import { sendPushNotification } from 'common/src/api/services/mobile_notifications';

const PendingTaskerManagementPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedTasker, setSelectedTasker] = useState<Tasker | null>(null);
  const [taskerCategories, setTaskerCategories] = useState<{ [key: number]: string[] }>({});
  const [taskers, setTaskers] = useState<Tasker[]>([]);
  const [total, setTotal] = useState(0);
  const pageSize = 50;
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);

  useEffect(() => {
    fetchCategoriesData();
    fetchTaskersData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedCategories]);

  const fetchCategoriesData = async () => {
    try {
      const fetchedCategories = await fetchCategories();
      setCategories(fetchedCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Error al cargar las categorías');
    }
  };

  const fetchTaskersData = async () => {
    setLoading(true);
    try {
      console.log('Fetching pending taskers for page:', page);
      const { taskers: fetchedTaskers, total: totalTaskers } = await fetchPendingTaskersPaginated(
        page,
        pageSize,
        selectedCategories
      );
      console.log('Fetched taskers:', fetchedTaskers);
      console.log('Total taskers:', totalTaskers);

      const categoriesPromises = fetchedTaskers.map((tasker) =>
        fetchTaskerCategories(tasker.id).then((categories) => ({
          taskerId: tasker.id,
          categories,
        }))
      );
      const categoriesResults = await Promise.all(categoriesPromises);
      const categoriesMap = Object.fromEntries(
        categoriesResults.map(({ taskerId, categories }) => [taskerId, categories])
      );

      setTaskerCategories(categoriesMap);
      setTaskers(fetchedTaskers);
      setTotal(totalTaskers);
    } catch (error) {
      console.error('Error fetching pending taskers:', error);
      toast.error('Error al cargar los taskers pendientes');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (tasker: Tasker) => {
    try {
      await approveTasker(tasker.id);
      // TODO: Enable email notifications after Supabase migration
      // await sendTaskerStatusEmail(tasker, true);

      // Enviar notificación push al tasker aprobado
      if (tasker.auth_id) {
        await sendPushNotification(
          tasker.auth_id,
          '¡Felicidades!',
          'Tu solicitud para ser Tasker ha sido aprobada. ¡Bienvenido al equipo de Tasky!',
          {
            type: 'tasker_approved',
            taskerId: tasker.id,
          }
        );
      }

      toast.success('Tasker aprobado exitosamente');
      setTaskers((prevTaskers) => prevTaskers.filter((t) => t.id !== tasker.id));
      setTotal((prevTotal) => prevTotal - 1);
    } catch (error) {
      console.error('Error approving tasker:', error);
      toast.error('Error al aprobar el tasker');
    }
  };

  const handleReject = async (tasker: Tasker) => {
    try {
      await rejectTasker(tasker.id);
      // TODO: Enable email notifications after Supabase migration
      // await sendTaskerStatusEmail(tasker, false);
      toast.success('Tasker rechazado exitosamente');
      setTaskers((prevTaskers) => prevTaskers.filter((t) => t.id !== tasker.id));
      setTotal((prevTotal) => prevTotal - 1);
    } catch (error) {
      console.error('Error rejecting tasker:', error);
      toast.error('Error al rechazar el tasker');
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleView = (tasker: Tasker) => {
    setSelectedTasker(tasker);
    setViewModalVisible(true);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Gestión de Taskers Pendientes</h1>
        <Dropdown>
          <DropdownTrigger>
            <Button>Filtrar por Categoría</Button>
          </DropdownTrigger>
          <DropdownMenu
            selectionMode="multiple"
            selectedKeys={new Set(selectedCategories)}
            onSelectionChange={(keys) => setSelectedCategories(Array.from(keys as Set<number>))}
          >
            {categories.map((category) => (
              <DropdownItem key={category.id}>{category.name}</DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>
      {loading ? (
        <SkeletonLoader />
      ) : (
        <Table aria-label="Tabla de Taskers Pendientes">
          <TableHeader>
            <TableColumn>ID</TableColumn>
            <TableColumn>Nombre</TableColumn>
            <TableColumn>Apellido</TableColumn>
            <TableColumn>Email</TableColumn>
            <TableColumn>Teléfono</TableColumn>
            <TableColumn>Universidad</TableColumn>
            <TableColumn>Carrera</TableColumn>
            <TableColumn>Categorías</TableColumn>
            <TableColumn>Acciones</TableColumn>
          </TableHeader>
          <TableBody>
            {taskers.map((tasker) => (
              <TableRow key={tasker.id}>
                <TableCell>{tasker.id}</TableCell>
                <TableCell>{tasker.name}</TableCell>
                <TableCell>{tasker.last_name}</TableCell>
                <TableCell>{tasker.email}</TableCell>
                <TableCell>{tasker.phone_number}</TableCell>
                <TableCell>{tasker.university}</TableCell>
                <TableCell>{tasker.carreer}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {taskerCategories[tasker.id]?.map((category, index) => (
                      <Chip key={index} size="sm">
                        {category}
                      </Chip>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-nowrap gap-2">
                    <Button size="sm" color="primary" onPress={() => handleView(tasker)}>
                      Ver
                    </Button>
                    <Button size="sm" color="success" onPress={() => handleApprove(tasker)}>
                      Aprobar
                    </Button>
                    <Button size="sm" color="danger" onPress={() => handleReject(tasker)}>
                      Rechazar
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className="flex justify-center mt-4">
        <Pagination
          total={Math.ceil(total / pageSize)}
          page={page}
          onChange={handlePageChange}
          showControls
          showShadow
          color="secondary"
          initialPage={1}
        />
      </div>
      <ModalViewTasker
        isOpen={viewModalVisible}
        onApprove={handleApprove}
        onClose={() => setViewModalVisible(false)}
        tasker={selectedTasker}
        taskerCategories={selectedTasker ? taskerCategories[selectedTasker.id] || [] : []}
      />
    </div>
  );
};

export default PendingTaskerManagementPage;
